import JSZip from 'jszip';
// import { saveAs } from 'file-saver';
const saveAs = (blob: Blob | string, name: string) => {
  const url = typeof blob === 'string' ? blob : URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  if (typeof blob !== 'string') URL.revokeObjectURL(url);
};
import { MenuData } from '../types';

export const compressImage = (base64Str: string, maxWidth = 800, quality = 0.7): Promise<string> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = base64Str;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
          resolve(base64Str); // Fail safe
          return;
      }
      
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => resolve(base64Str); // Fail safe
  });
};

export const downloadImagesZip = async (data: MenuData, onProgress?: (percent: number) => void) => {
  const zip = new JSZip();
  const imagesFolder = zip.folder("images");
  
  if (!imagesFolder) {
      console.error("Failed to create images folder in zip");
      return;
  }

  const sanitize = (name: string) => {
    return name.replace(/[\/\\:*?"<>|]/g, "").trim().replace(/\s+/g, " ");
  };

  const fetchImage = async (url: string): Promise<{ data: ArrayBuffer; ext: string } | null> => {
    try {
      const response = await fetch(url);
      if (!response.ok) return null;
      
      const blob = await response.blob();
      const arrayBuffer = await blob.arrayBuffer();
      
      let ext = 'jpg'; // Default fallback
      const contentType = response.headers.get('content-type');
      
      if (contentType) {
          if (contentType.includes('png')) ext = 'png';
          else if (contentType.includes('webp')) ext = 'webp';
          else if (contentType.includes('gif')) ext = 'gif';
          else if (contentType.includes('jpeg')) ext = 'jpg';
          else if (contentType.includes('svg')) ext = 'svg';
      }
      
      // Try to get extension from URL if possible (for better accuracy if content-type is generic)
      try {
          // Handle data URLs
          if (url.startsWith('data:')) {
              const mime = url.split(';')[0].split(':')[1];
              if (mime) {
                  if (mime.includes('png')) ext = 'png';
                  else if (mime.includes('webp')) ext = 'webp';
                  else if (mime.includes('gif')) ext = 'gif';
                  else if (mime.includes('jpeg')) ext = 'jpg';
                  else if (mime.includes('svg')) ext = 'svg';
              }
          } else {
              const urlObj = new URL(url);
              const pathname = urlObj.pathname;
              const dotIndex = pathname.lastIndexOf('.');
              if (dotIndex !== -1) {
                  const urlExt = pathname.substring(dotIndex + 1).toLowerCase();
                  if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg'].includes(urlExt)) {
                      ext = urlExt === 'jpeg' ? 'jpg' : urlExt;
                  }
              }
          }
      } catch (e) {
          // Ignore URL parsing errors
      }

      return { data: arrayBuffer, ext };
    } catch (error) {
      console.error(`Failed to fetch image: ${url}`, error);
      return null;
    }
  };

  const promises: Promise<void>[] = [];
  let completed = 0;
  
  // Calculate total items to download (items with images + logo)
  let totalItems = 0;
  if (data.logo) totalItems++;
  data.categories.forEach(c => {
      c.items.forEach(i => {
          if (i.image) totalItems++;
      });
  });

  const updateProgress = () => {
      completed++;
      if (onProgress && totalItems > 0) {
          onProgress(Math.round((completed / totalItems) * 100));
      }
  };

  // 1. Download Logo
  if (data.logo) {
      const logoFolder = zip.folder("logo");
      if (logoFolder) {
          const p = (async () => {
              const imgData = await fetchImage(data.logo!);
              if (imgData) {
                  logoFolder.file(`logo.${imgData.ext}`, imgData.data);
              }
              updateProgress();
          })();
          promises.push(p);
      }
  }

  // 2. Download Menu Items
  for (const category of data.categories) {
    const catName = sanitize(category.name.en || 'Untitled Category');
    const catFolder = imagesFolder.folder(catName);
    
    if (!catFolder) continue;

    const reservedNames = new Set<string>();

    for (const item of category.items) {
      if (!item.image) continue;

      let baseName = sanitize(item.name.en || 'Untitled Item');
      let finalName = baseName;
      let counter = 2;
      
      while (reservedNames.has(finalName)) {
          finalName = `${baseName} (${counter})`;
          counter++;
      }
      reservedNames.add(finalName);

      const p = (async () => {
          const imgData = await fetchImage(item.image!);
          if (imgData) {
              catFolder.file(`${finalName}.${imgData.ext}`, imgData.data);
          }
          updateProgress();
      })();
      promises.push(p);
    }
  }

  await Promise.all(promises);

  const content = await zip.generateAsync({ type: "blob" });
  const fileName = `${sanitize(data.title?.en || 'menu')}-images.zip`;
  saveAs(content, fileName);
};
